/**
 * HoverPopover — hover-triggered tooltip card. The wrapped child IS the
 * trigger; pass `content` for the popover body. Styled to match InfoPopover.
 *
 * Two variants:
 *   - "card" (default) — white bordered card, edge-aligned, for rich content.
 *   - "tooltip"        — compact dark pill centered below the trigger with a
 *     caret, opening on hover-intent (600ms). Flips above when the measured
 *     pill would clip the bottom of the viewport, and clamps horizontally —
 *     the caret keeps pointing at the trigger in every case.
 *
 * Portal-rendered to <body> so it escapes ancestor `overflow: hidden`.
 * Placement auto-flips when there isn't enough room.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const OPEN_DELAY_MS = 150;          // card variant — rich hover cards stay eager
const TOOLTIP_OPEN_DELAY_MS = 600;  // tooltip variant — hover-intent (native title ≈ 500ms)
const GAP_PX = 6;

export default function HoverPopover({
  children,
  content,
  width = 260,
  side = 'left',         // align popover's edge to trigger's edge: 'left' | 'right'
  placement = 'bottom',  // 'top' | 'bottom' (auto-flips when no room)
  variant = 'card',      // 'card' | 'tooltip' (dark pill, centered, caret)
  openDelay,             // ms; defaults per variant (tooltip 600, card 150)
  disabled = false,
}) {
  const resolvedDelay = openDelay ?? (variant === 'tooltip' ? TOOLTIP_OPEN_DELAY_MS : OPEN_DELAY_MS);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null); // { top, left, placement }
  const openTimerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Reposition on scroll/resize so the card tracks its trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;

    const compute = () => {
      let rect = triggerRef.current?.getBoundingClientRect();
      // Some flex/sticky contexts collapse the wrapper span to zero height —
      // anchor to the actual trigger element instead so placement stays true.
      if (rect && rect.height === 0 && triggerRef.current?.firstElementChild) {
        rect = triggerRef.current.firstElementChild.getBoundingClientRect();
      }
      if (!rect) return;

      // Use viewport height as the flip ceiling — avoids a measure-then-paint pass.
      const vh = window.innerHeight;

      if (variant === 'tooltip') {
        // Dark pill: centered on the trigger, on the requested side (`placement`).
        // This pass is a pre-paint guess (60px height estimate); the measured
        // pass below (tipRef effect) flips/clamps against the real pill size.
        const above = placement === 'bottom'
          ? (vh - rect.bottom <= 60 && rect.top > 60)
          : rect.top > 60;
        setCoords({
          top: above ? rect.top - GAP_PX : rect.bottom + GAP_PX,
          left: rect.left + rect.width / 2,
          placement: above ? 'top' : 'bottom',
          anchorTop: rect.top,
          anchorBottom: rect.bottom,
        });
        return;
      }

      const spaceBelow = vh - rect.bottom;
      const spaceAbove = rect.top;
      const wantTop = placement === 'top';
      const fitsBelow = spaceBelow > 200;
      const fitsAbove = spaceAbove > 200;
      const actualPlacement = wantTop
        ? (fitsAbove ? 'top' : 'bottom')
        : (fitsBelow ? 'bottom' : (fitsAbove ? 'top' : 'bottom'));

      const left = side === 'right'
        ? Math.max(8, rect.right - width)
        : Math.min(window.innerWidth - width - 8, Math.max(8, rect.left));
      const top = actualPlacement === 'top'
        ? rect.top  // popover sits ABOVE; its bottom edge ends GAP_PX above this
        : rect.bottom + GAP_PX;

      setCoords({ top, left, placement: actualPlacement });
    };

    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, placement, side, width, variant]);

  // Tooltip variant, measured pass (useLayoutEffect = runs before paint, so no
  // flicker): with the pill's REAL size, (1) flip to the other side when the
  // guessed side would clip the viewport and the other side fits, (2) clamp
  // horizontally, (3) re-aim the caret at the trigger after any clamp.
  const tipRef = useRef(null);
  useLayoutEffect(() => {
    if (variant !== 'tooltip' || !open || !coords || !tipRef.current) return;
    const el = tipRef.current;
    const PAD = 8;
    const { width: w, height: h } = el.getBoundingClientRect();

    const fitsBelow = coords.anchorBottom + GAP_PX + h <= window.innerHeight - PAD;
    const fitsAbove = coords.anchorTop - GAP_PX - h >= PAD;
    const side = coords.placement === 'bottom' && !fitsBelow && fitsAbove ? 'top'
      : coords.placement === 'top' && !fitsAbove && fitsBelow ? 'bottom'
      : coords.placement; // neither fits → keep the guess (can't do better)
    if (side !== coords.placement) {
      // Correct the side and bail; this effect re-runs with the new coords.
      // No loop: we only flip TO a side that fits.
      setCoords((c) => ({
        ...c,
        placement: side,
        top: side === 'top' ? c.anchorTop - GAP_PX : c.anchorBottom + GAP_PX,
      }));
      return;
    }

    const half = w / 2;
    let left = coords.left;
    if (left + half > window.innerWidth - PAD) left = window.innerWidth - PAD - half;
    if (left - half < PAD) left = PAD + half;
    el.style.left = `${left}px`;
    // Keep the caret pointing at the trigger even when the pill was clamped
    // (bounded so it never escapes the pill's rounded corners).
    const caret = el.querySelector('[data-tip-caret]');
    if (caret) caret.style.left = `${Math.max(8, Math.min(w - 8, half + (coords.left - left)))}px`;
  }, [variant, open, coords]);

  // Nothing to show → render the trigger transparently.
  if (disabled || content == null || content === '') {
    return <>{children}</>;
  }

  const handleMouseEnter = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => setOpen(true), resolvedDelay);
  };

  const handleMouseLeave = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setOpen(false);
  };

  // Top placement: translate the portal upward by its own height so its
  // bottom edge sits GAP_PX above the trigger.
  const portalStyle = coords
    ? {
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        width,
        zIndex: 200,
        ...(coords.placement === 'top'
          ? { transform: `translateY(calc(-100% - ${GAP_PX}px))` }
          : {}),
      }
    : { display: 'none' };

  // Clicking the trigger dismisses the tooltip immediately — it usually opens
  // a menu/modal over the trigger, and mouseleave never fires under an overlay,
  // which left the tooltip stranded on screen.
  const handleClickCapture = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setOpen(false);
  };

  return (
    <span
      ref={triggerRef}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      onClickCapture={handleClickCapture}
    >
      {children}
      {open && variant === 'tooltip' && ReactDOM.createPortal(
        coords ? (
          <div
            ref={tipRef}
            role="tooltip"
            className="pointer-events-none rounded-md px-2 py-[3px] text-[10px] font-semibold text-white"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: coords.placement === 'top' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
              background: '#08111f',
              zIndex: 9999,
              // Pill for short labels; wraps into a compact box for longer hints.
              width: 'max-content',
              maxWidth: 240,
              whiteSpace: 'normal',
              textAlign: 'center',
              lineHeight: 1.45,
            }}
          >
            {content}
            <div
              data-tip-caret
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                ...(coords.placement === 'top'
                  ? { top: '100%', borderTop: '4px solid #08111f' }
                  : { bottom: '100%', borderBottom: '4px solid #08111f' }),
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
              }}
            />
          </div>
        ) : null,
        document.body,
      )}
      {open && variant !== 'tooltip' && ReactDOM.createPortal(
        <div
          role="tooltip"
          style={{ ...portalStyle, pointerEvents: 'none' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="bg-white rounded-[10px] border p-3 text-[11.5px] leading-relaxed text-[#08111f]"
            style={{
              borderColor: '#e8e2d6',
              boxShadow: '0 12px 32px rgba(8,17,31,.18), 0 0 0 1px rgba(221,214,198,.4)',
              pointerEvents: 'auto',
            }}
          >
            {content}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
